"use client"

import * as React from "react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import * as z from "zod"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage,
} from "@/components/ui/form"
import { toast } from "sonner"
import { MessageSquareQuote } from "lucide-react"
import { useUserContext } from "@/components/providers/index"
import { createFeedback } from "@/lib/supabase/queries/feedback-supabase-query"
// import { sendFeedbackEmail } from "@/lib/email/server-actions"
import { submitSupportMessage } from "@/lib/support/submit-support-message"
import {
    DialogDrawerSwitch,
    DialogDrawerSwitchContent,
    DialogDrawerSwitchTrigger
} from "@/components/ui/dialog-drawer-switch"
import { cn } from "@/lib/utils"

// Feedback schema with validation
const feedbackSchema = z.object({
    message: z.string()
        .min(1, { message: "Message must be at least 1 character" })
        .max(5000, { message: "Message must not exceed 5000 characters" }),
})

type FeedbackFormData = z.infer<typeof feedbackSchema>

export function Feedback({ isCollapsed = false }: { isCollapsed?: boolean }) {
    const [open, setOpen] = React.useState(false)
    const [isSubmitting, setIsSubmitting] = React.useState(false)
    const { userContextValue } = useUserContext()

    const form = useForm<FeedbackFormData>({
        resolver: zodResolver(feedbackSchema),
        defaultValues: {
            message: "",
        },
        mode: "onChange",
    })

    async function handleSubmit(data: FeedbackFormData) {
        if (!userContextValue?.id) {
            toast.error("You must be logged in to submit feedback")
            return
        }

        setIsSubmitting(true)
        
        try {
            // Save to database first
            // await createFeedback({
            //     category: "feedback",
            //     message: data.message,
            // })
            
            // // Then send notification email using server action
            // await sendFeedbackEmail({
            //     message: data.message,
            //     userId: userContextValue.id,
            //     userName: userContextValue.displayName || userContextValue.email,
            //     userEmail: userContextValue.email
            // })

            await submitSupportMessage({
                subject: null,
                message: data.message,
                userId: userContextValue.id,
                userName: userContextValue.displayName || userContextValue.email,
                userEmail: userContextValue.email,
                category: "feedback"
            })

            toast.success("Thank you for your feedback!")
            form.reset()
            setOpen(false)
        } catch (error) {
            console.error("Error handling feedback:", error)
            toast.error("Failed to submit feedback. Please try again.")
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <DialogDrawerSwitch open={open} onOpenChange={setOpen} desktopComponent="popover">
            <DialogDrawerSwitchTrigger asChild desktopComponent="popover">
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={cn(`h-8 ${isCollapsed ? 'justify-center h-8 w-8' : 'w-full justify-center items-center gap-2 px-2'} border border-border`)}
                >
                    <MessageSquareQuote className="size-4" />
                    {!isCollapsed && "Feedback"}
                </Button>
            </DialogDrawerSwitchTrigger>
            
            <DialogDrawerSwitchContent 
                desktopComponent="popover"
                align="end" 
                side="right" 
                className="w-full md:w-72 p-2"
            >
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                        <FormField
                            control={form.control}
                            name="message"
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <Textarea
                                            placeholder="What works well? What doesn't? Please be specific!"
                                            className="min-h-[120px] resize-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-input hover:border-input outline-none placeholder:text-sm"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        
                        <div className="flex items-start justify-between">
                            <div className="flex flex-col items-start justify-between">
                                <span className="text-xs text-muted-foreground">
                                    Submit a feature request <Link href="/feedback" className="text-blue-500 hover:underline">here</Link>
                                </span>
                                <span className="text-xs text-muted-foreground">
                                    Need help? <Link href="/support" className="text-blue-500 hover:underline">Contact us</Link>
                                </span>
                            </div>
                            
                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                className="text-xs px-2 py-1 h-8"
                            >
                                {isSubmitting ? "Sending..." : "Send"}
                            </Button>
                        </div>
                    </form>
                </Form>
            </DialogDrawerSwitchContent>
        </DialogDrawerSwitch>
    )
} 